import type {
  AgentRow,
  DashSchedule,
  DashboardData,
  DispositionKey,
  Period,
  ReasonRow,
  ReviewRow,
} from './types';
import { recallCallsExpiringToday } from '../triage/recallCalls';

/**
 * The fixtures from spec 17 §12. The boards are drawn with a consistent fake
 * dataset **so every identity in §7 holds** — if this reproduces the boards
 * exactly, the identities are wired correctly.
 */

/* ---- RIGHT NOW: identical on both periods, by construction. --------------- *
 * These live outside the per-period payloads rather than being copied into each
 * one, because §11 asks us to verify the four always-now panels are identical
 * across the picker. Sharing the object makes that true rather than tested.
 * -------------------------------------------------------------------------- */

/** 148 h of 196 h, 22 open gaps — 14 worked, 5 no-reason + 3 no-rule = 8
 * unreachable (spec 42). Per-day booked sums to 148. */
const SCHEDULE: DashSchedule = {
  state: 'ok',
  days: [
    { label: 'Today', date: '28 Sep', booked: 27, capacity: 30, openGaps: 1 },
    { label: 'Tue', date: '29 Sep', booked: 24, capacity: 30, openGaps: 3 },
    { label: 'Wed', date: '30 Sep', booked: 29, capacity: 30, openGaps: 0 },
    { label: 'Thu', date: '1 Oct', booked: 19, capacity: 30, openGaps: 5 },
    { label: 'Fri', date: '2 Oct', booked: 22, capacity: 30, openGaps: 4 },
    { label: 'Sat', date: '3 Oct', booked: 11, capacity: 16, openGaps: 2 },
    { label: 'Mon', date: '5 Oct', booked: 16, capacity: 30, openGaps: 7 },
  ],
  gapsWorked: 14,
  gapsNoReason: 5,
  gapsNoRule: 3,
  gapsUnreachable: 8,
};

/** 11 + 9 + 6 + 3 = 29, which is the Train ELVA nav badge. One number, one source. */
const REVIEW: ReviewRow[] = [
  { label: 'Questions it could not answer', count: 11, queue: 'questions' },
  { label: 'Replies it was not sure about', count: 9, queue: 'replies' },
  { label: 'Sentences it could not match', count: 6, queue: 'sentences' },
  { label: 'Bookings a human flagged', count: 3, queue: 'bookings' },
];

/** Three appointment reasons have no role mapped, so ELVA cannot book them at all. */
const UNCOVERED_REASONS = 3;

/** Three people are still waiting for a person, right now, on both periods. */
const STILL_WAITING_NOW = 3;

/* ---- Attention, also always-now (P344). ---------------------------------- */

/** Fixed `since` values so the handoff clock is testable either side of its
 * boundary. Recall's handoff is deliberately past 7 days, so the board's amber
 * `12 stalled at the human-call rung` is what the clock produces, not a literal. */
const NOW = Date.parse('2026-09-28T10:42:00Z');
const daysAgo = (n: number) => new Date(NOW - n * 86400000).toISOString();
const minsAgo = (n: number) => new Date(NOW - n * 60000).toISOString();

/* ---- Period payloads ----------------------------------------------------- */

const REASONS_24H: ReasonRow[] = [
  { reason: 'Hygiene — recall cleaning', booked: 8, asked: 9, valueCents: 124000 },
  { reason: 'New patient exam', booked: 6, asked: 7, valueCents: 174000 },
  { reason: 'Emergency — tooth pain', booked: 4, asked: 4, valueCents: 90000 },
  { reason: 'Crown — prep or seat', booked: 3, asked: 3, valueCents: 387000 },
  { reason: 'Filling', booked: 3, asked: 3, valueCents: 79500 },
  { reason: 'Invisalign consult', booked: 2, asked: 2, valueCents: 36000 },
  { reason: 'Denture or partial', booked: 1, asked: 2, valueCents: 94000 },
  { reason: 'Implant consult', booked: 1, asked: 1, valueCents: 24000 },
];

const REASONS_30D: ReasonRow[] = [
  { reason: 'Hygiene — recall cleaning', booked: 214, asked: 231, valueCents: 3317000 },
  { reason: 'New patient exam', booked: 121, asked: 134, valueCents: 3509000 },
  { reason: 'Filling', booked: 88, asked: 91, valueCents: 2332000 },
  { reason: 'Emergency — tooth pain', booked: 71, asked: 78, valueCents: 1597500 },
  { reason: 'Crown — prep or seat', booked: 54, asked: 56, valueCents: 6966000 },
  { reason: 'Invisalign consult', booked: 38, asked: 44, valueCents: 684000 },
  { reason: 'Implant consult', booked: 21, asked: 23, valueCents: 504000 },
  { reason: 'Night guard', booked: 17, asked: 18, valueCents: 986000 },
  { reason: 'Denture or partial', booked: 14, asked: 16, valueCents: 1316000 },
  { reason: 'Extraction', booked: 13, asked: 14, valueCents: 370500 },
];

const DISPOSITIONS_24H: Record<DispositionKey, number> = {
  booked: 19,
  moved: 7,
  answered: 14,
  message: 11,
  warm_transfer: 6,
  urgent_triage: 2,
  cancelled: 4,
};

const DISPOSITIONS_30D: Record<DispositionKey, number> = {
  booked: 446,
  moved: 165,
  answered: 325,
  message: 245,
  warm_transfer: 133,
  urgent_triage: 39,
  cancelled: 95,
};

/**
 * The agent table. Nine agents (spec 36): receptionist, SMS, email, then the six.
 *
 * A/R is `blocked`: no payment link connected, so nothing can be sent at all. Its
 * name, icon and figures render muted, and `blocked` is the only kind that changes
 * the state chip.
 */
function agents(period: Period): AgentRow[] {
  const is24 = period === '24h';
  return [
    {
      // The old AI Receptionist card is now three agents (spec 36 §7): calls, texts
      // and emails each stand on their own, with their own numbers.
      key: 'receptionist',
      name: 'AI Receptionist',
      icon: 'headphones',
      state: 'working',
      figures: [
        { value: { state: 'ok', value: is24 ? 24 : 612 }, label: 'calls' },
        { value: { state: 'ok', value: is24 ? 9 : 298 }, label: 'booked' },
      ],
      attention: [
        { kind: 'handoff', code: 'waiting_for_a_human', count: STILL_WAITING_NOW, since: minsAgo(8), target: '/triage' },
      ],
      target: '/ai-team/receptionist',
    },
    {
      key: 'sms',
      name: 'AI SMS',
      icon: 'chat',
      state: 'working',
      figures: [
        { value: { state: 'ok', value: is24 ? 30 : 690 }, label: 'conversations' },
        { value: { state: 'ok', value: is24 ? 8 : 118 }, label: 'booked' },
      ],
      attention: [{ kind: 'clear', code: 'clear', count: 0, since: minsAgo(0), target: '/messages' }],
      target: '/messages',
    },
    {
      key: 'email',
      name: 'AI Email',
      icon: 'mail',
      state: 'working',
      figures: [
        { value: { state: 'ok', value: is24 ? 9 : 146 }, label: 'emails' },
        { value: { state: 'ok', value: is24 ? 2 : 30 }, label: 'booked' },
      ],
      attention: [{ kind: 'clear', code: 'clear', count: 0, since: minsAgo(0), target: '/email' }],
      target: '/email',
    },
    {
      key: 'reminders',
      name: 'Appointment Reminders',
      icon: 'bell',
      state: 'working',
      figures: [
        { value: { state: 'ok', value: is24 ? 118 : 2584 }, label: 'sent' },
        { value: { state: 'ok', value: is24 ? 94 : 812 }, label: 'confirmed' },
      ],
      attention: [
        { kind: 'stalled', code: 'unconfirmed_inside_24h', count: 6, since: minsAgo(220), target: '/' },
        // Lower precedence, so it does not render — but it is real, and keeping it
        // in the fixture is what proves precedence rather than asserting it.
        { kind: 'handoff', code: 'post_op_for_a_human', count: 2, since: minsAgo(40), target: '/triage' },
      ],
      target: '/ai-team/reminders',
    },
    {
      key: 'recall',
      name: 'Recall',
      icon: 'refresh',
      state: 'working',
      figures: [
        { value: { state: 'ok', value: is24 ? 46 : 1140 }, label: 'contacted' },
        { value: { state: 'ok', value: is24 ? 5 : 131 }, label: 'booked' },
      ],
      // A recall call expires at the Recall setting's value and the patient moves
      // on, so there is no "stalled past seven days" state to show (amendment 20
      // §5). What is real is the calls that run out TODAY and nobody has made.
      attention: [
        {
          kind: 'stalled',
          code: 'human_calls_expiring',
          // 2 of the 7, counted from the queue itself (amendment 24 §7). It was
          // a literal 12, against a page saying 7 and a Triage tab saying 4.
          count: recallCallsExpiringToday(),
          since: daysAgo(1),
          target: '/triage?source=recall',
        },
      ],
      target: '/ai-team/recall',
    },
    {
      key: 'gap_filler',
      name: 'Gap Filler',
      icon: 'clock',
      state: 'working',
      figures: [
        { value: { state: 'ok', value: is24 ? 9 : 112 }, label: 'gaps found' },
        { value: { state: 'ok', value: is24 ? 4 : 74 }, label: 'filled' },
      ],
      // The same count as the schedule footer's unreachable gaps — one number from
      // one source (§6.5), so it is read from SCHEDULE rather than written twice.
      attention: [
        {
          kind: 'stalled',
          code: 'unreachable_gaps',
          count: SCHEDULE.gapsUnreachable,
          since: daysAgo(3),
          target: '/settings/appointment-reasons',
        },
      ],
      target: '/ai-team/gap-filler',
    },
    {
      key: 'treatment',
      name: 'Treatment Coordinator',
      icon: 'gem',
      state: 'working',
      figures: [
        { value: { state: 'ok', value: is24 ? 12 : 288 }, label: 'messaged' },
        { value: { state: 'ok', value: is24 ? 2 : 47 }, label: 'booked' },
      ],
      money: { value: { state: 'ok', value: is24 ? 318000 : 9640000 }, label: 'booked' },
      // Amendment 28 §5: the over-$1,000 held row is A/R's, not TC's. Fix 29 §6: the
      // TC right column is simply who needs a person (a call-me tap or a question).
      attention: [
        { kind: 'handoff', code: 'tc_needs_person', count: 2, since: daysAgo(0), target: '/calls?type=treatment' },
      ],
      target: '/ai-team/treatment',
    },
    {
      key: 'invisalign',
      name: 'Invisalign Coordinator',
      icon: 'sparkle',
      state: 'working',
      // Amendment 30 §8: prospects · ready · consults booked (90 days) — the same
      // numbers the agent's Overview reads (INV_NUMBERS).
      figures: is24
        ? [
            { value: { state: 'ok', value: 7 }, label: 'ready' },
            { value: { state: 'ok', value: 2 }, label: 'coming in' },
          ]
        : [
            { value: { state: 'ok', value: 63 }, label: 'prospects' },
            { value: { state: 'ok', value: 7 }, label: 'ready' },
            { value: { state: 'ok', value: 8 }, label: 'consults booked (90 days)' },
          ],
      // The right column: 7 ready for a nudge → the Call list.
      attention: [{ kind: 'stalled', code: 'ready_for_nudge', count: 7, since: daysAgo(2), target: '/calls?type=invisalign' }],
      target: '/ai-team/invisalign',
    },
    {
      // Amendment 28 §5: A/R is rebuilt and working — 82 followed up, $11,240
      // collected. The Blocked · no-payment-link state is kept as a design in the
      // vocabulary (and the blocked codes remain registered), but the working mock
      // shows the real practice.
      key: 'ar',
      name: 'Accounts receivable',
      icon: 'dollar',
      state: 'working',
      figures: [
        { value: { state: 'ok', value: is24 ? 14 : 82 }, label: is24 ? 'messaged' : 'followed up' },
        { value: { state: 'ok', value: is24 ? 5 : 74 }, label: 'paid' },
      ],
      money: { value: { state: 'ok', value: is24 ? 86000 : 1124000 }, label: 'collected' },
      attention: [
        { kind: 'handoff', code: 'ar_needs_person', count: 11, since: daysAgo(0), target: '/calls?type=balance' },
      ],
      target: '/ai-team/ar',
    },
  ];
}

/** Past 24 hours — 63 conversations, 28 appointments, $10,085, 8 needed a human. */
export const DASHBOARD_24H: DashboardData = {
  period: '24h',
  asOf: '10:42',
  windowLabel: '28 Sep 10:42 back to 27 Sep 10:42',
  tiles: {
    conversations: { state: 'ok', value: 63 },
    afterHours: 20,
    appointments: { state: 'ok', value: 28 },
    appointmentsBy: { receptionist: 9, sms: 8, email: 2, recall: 5, gapFiller: 4 },
    productionCents: { state: 'ok', value: 1008500 },
    reschedules: 7,
    neededAHuman: { state: 'ok', value: 8 },
    warmTransfers: 6,
    urgentTriage: 2,
    stillWaitingNow: STILL_WAITING_NOW,
  },
  dispositions: DISPOSITIONS_24H,
  reasons: REASONS_24H,
  schedule: SCHEDULE,
  agents: agents('24h'),
  review: REVIEW,
  restraint: {
    optedOut: 2,
    outboundTotal: 183,
    heldByCap: { state: 'ok', value: 14 },
    uncoveredReasons: UNCOVERED_REASONS,
  },
};

/** Past 30 days — 1,448 conversations, 651 appointments, $215,820, 172 needed a human. */
export const DASHBOARD_30D: DashboardData = {
  period: '30d',
  asOf: '10:42',
  windowLabel: '30 Aug – 28 Sep 2026',
  tiles: {
    conversations: { state: 'ok', value: 1448 },
    afterHours: 466,
    appointments: { state: 'ok', value: 651 },
    appointmentsBy: { receptionist: 298, sms: 118, email: 30, recall: 131, gapFiller: 74 },
    productionCents: { state: 'ok', value: 21582000 },
    reschedules: 165,
    neededAHuman: { state: 'ok', value: 172 },
    warmTransfers: 133,
    urgentTriage: 39,
    stillWaitingNow: STILL_WAITING_NOW,
  },
  dispositions: DISPOSITIONS_30D,
  reasons: REASONS_30D,
  schedule: SCHEDULE,
  agents: agents('30d'),
  review: REVIEW,
  restraint: {
    optedOut: 27,
    outboundTotal: 4806,
    heldByCap: { state: 'ok', value: 392 },
    uncoveredReasons: UNCOVERED_REASONS,
  },
};

export function dashboardFor(period: Period): DashboardData {
  return period === '30d' ? DASHBOARD_30D : DASHBOARD_24H;
}
